import React from "react";

/**
 * AppErrorBoundary — catches any uncaught React render error in its subtree
 * and shows a polite "something broke" message instead of unmounting the
 * whole app and showing a black screen.
 *
 * Critical safety net: a single null-access in one feature panel must never
 * leave the advisor staring at a blank screen mid-shift. Logs the error to
 * the console (and the optional `onError` prop) so it can be diagnosed.
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("AppErrorBoundary caught:", error, info?.componentStack);
    if (typeof this.props.onError === "function") {
      try { this.props.onError(error, info); } catch (_) { /* swallow */ }
    }
    // Fire-and-forget crash alert so Steve hears about snags instantly.
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/crash-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          message: error?.message || String(error || "Unknown error"),
          stack: error?.stack || "",
          component_stack: info?.componentStack || "",
          url: window.location.href,
          user_agent: navigator.userAgent,
        }),
      }).catch(() => {});
    } catch (_) { /* never let reporting break the fallback UI */ }
  }

  reset = () => { this.setState({ error: null }); window.location.reload(); };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error || "Unknown error");
    return (
      <div className="min-h-screen bg-background grid place-items-center px-6" data-testid="app-error-boundary">
        <div className="max-w-md text-center space-y-3">
          <div className="label-overline text-danger">Something broke</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">We hit a snag rendering this screen</h1>
          <p className="text-sm text-muted-foreground">
            The app caught an error before the screen could finish loading. Your data is safe — try refreshing. If it
            keeps happening, send Steve the message below.
          </p>
          <pre className="text-left text-xs bg-muted/40 border border-border rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words font-mono-plex" data-testid="error-message">
            {msg}
          </pre>
          <div className="flex items-center justify-center gap-2 pt-2">
            <button onClick={this.reset} className="btn-primary" data-testid="error-reload-btn">Refresh</button>
            <button onClick={() => { window.history.back(); }} className="btn-outline" data-testid="error-back-btn">Go back</button>
          </div>
        </div>
      </div>
    );
  }
}
